import { useMemo, useState } from 'react'
import { Box, MenuItem, Select, Stack } from '@mui/material'
import dayjs from 'dayjs'
import usePractitionerReportInsightsSeries from './usePractitionerReportInsightsSeries'
import {
  DeltaStat,
  INSIGHT_COLORS,
  InsightsCard,
  InsightsLoading,
  SectionEmpty,
  TrajectoryChart,
} from './insightsCharts'

// Headline scalar metrics per reportType (read from data.totals). First entry is the trajectory default.
const METRICS_BY_TYPE = {
  prescription: [
    { key: 'reports', label: 'Consultations' },
    { key: 'patients', label: 'Patients' },
    { key: 'new', label: 'New' },
    { key: 'repeat', label: 'Repeat' },
  ],
  'therapy-report': [
    { key: 'reports', label: 'Reports' },
    { key: 'patients', label: 'Patients' },
  ],
  'screening-report': [
    { key: 'reports', label: 'Reports' },
    { key: 'patients', label: 'Patients' },
    { key: 'new', label: 'New' },
    { key: 'repeat', label: 'Repeat' },
  ],
  'patient-stats': [
    { key: 'patients', label: 'Patients' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'newPatients', label: 'New patients' },
    { key: 'repeatPatients', label: 'Repeat patients' },
  ],
}

function totalOf(data, key) {
  const v = data?.totals?.[key]
  return Number.isFinite(Number(v)) ? Number(v) : 0
}

function monthLabel(period) {
  return dayjs(period.from).format("MMM 'YY")
}

/**
 * Period comparison for one sub-tab, rendered above its single-period charts when Compare is on.
 *
 * The primary period's data is already fetched by the owning sub-tab and passed in as `primaryData`
 * (no duplicate call); only the `comparison.otherPeriods` are fetched here. Two modes:
 *  - pairwise  → the current period vs one other period, as per-metric delta tiles.
 *  - trajectory→ this month across previous years, as a line chart of a selectable metric.
 */
export default function PeriodComparison({
  active,
  reportType,
  discipline,
  clinicBrandId,
  clinicLocationId,
  primaryPeriod,
  primaryData,
  comparison,
}) {
  const metrics = useMemo(() => METRICS_BY_TYPE[reportType] || [], [reportType])
  const [metricKey, setMetricKey] = useState(metrics[0]?.key)

  const otherPeriods = comparison?.otherPeriods || []
  const { series: others, loading } = usePractitionerReportInsightsSeries({
    enabled: active && !!comparison && otherPeriods.length > 0,
    reportType,
    discipline,
    clinicBrandId,
    clinicLocationId,
    periods: otherPeriods,
  })

  // Chronological [others..., primary], leading no-data periods trimmed (so the line starts where
  // history actually begins). Keeps the primary even when empty.
  const trimmed = useMemo(() => {
    const full = [
      ...(others || []).map((o) => ({ period: o.period, data: o.data })),
      { period: primaryPeriod, data: primaryData },
    ]
    const hasAny = (d) => d && metrics.some((m) => totalOf(d, m.key) > 0)
    let start = 0
    while (start < full.length - 1 && !hasAny(full[start].data)) start += 1
    return full.slice(start)
  }, [others, primaryPeriod, primaryData, metrics])

  if (!comparison) return null

  const isTrajectory = comparison.mode === 'trajectory'
  const title = isTrajectory ? 'Growth trajectory' : 'Period comparison'
  const compareLabel = otherPeriods[0]?.label

  let body
  if (loading && !others) {
    body = <InsightsLoading />
  } else if (isTrajectory) {
    const points = trimmed.map((e) => ({ label: monthLabel(e.period), value: totalOf(e.data, metricKey) }))
    body = (
      <Box>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Select
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
            size="small"
            sx={{ fontSize: 13, minWidth: 150, '& .MuiSelect-select': { py: 0.5 } }}
          >
            {metrics.map((m) => (
              <MenuItem key={m.key} value={m.key} sx={{ fontSize: 13 }}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        {trimmed.length < 2 ? (
          <SectionEmpty label="Not enough history to chart a trajectory" />
        ) : (
          <TrajectoryChart points={points} color={INSIGHT_COLORS[0]} />
        )}
      </Box>
    )
  } else {
    const compareData = others?.[0]?.data
    body = (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {metrics.map((m, i) => (
          <DeltaStat
            key={m.key}
            label={m.label}
            current={totalOf(primaryData, m.key)}
            previous={totalOf(compareData, m.key)}
            color={INSIGHT_COLORS[i % INSIGHT_COLORS.length]}
          />
        ))}
      </Box>
    )
  }

  const subtitle = isTrajectory
    ? `${primaryPeriod.label} vs the same month in prior years`
    : `${primaryPeriod.label} vs ${compareLabel || '—'}`

  return (
    <InsightsCard title={title} subtitle={subtitle}>
      {body}
    </InsightsCard>
  )
}
